import React, { useEffect, useState } from 'react';
import { gql, useMutation } from '@apollo/client';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Stack,
  Chip,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { StagingResultsDialog } from './StagingResultsDialog';

const rollbackVenueDataImport = gql`
  mutation RollbackVenueDataImport($input: RollbackVenueDataImportInput!) {
    rollbackVenueDataImport(input: $input) {
      venueDataImport {
        id
        createdAt
        updatedAt
        status
        errorMessages
      }
      errors {
        message
      }
    }
  }
`;

interface VenueDataImport {
  id: string;
  createdAt: string;
  updatedAt: string;
  status: string;
  errorMessages: string[];
  stagingResults?: any;
  storageUrl?: string | null;
}

interface VenueDataImportsListProps {
  venueDataImports: VenueDataImport[];
  autoOpenImportId?: string;
}
const formatStatus = (status: string) =>
  status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

const statusColor = (status: string): ChipColor => {
  if (['completed', 'staged', 'rolled_back'].includes(status)) return 'success';
  if (['failed', 'staging_failed', 'rollback_failed', 'declined'].includes(status)) return 'error';
  if (['running', 'approved', 'rolling_back', 'rollback_started'].includes(status)) return 'info';
  return 'default';
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

export const VenueDataImportsList = ({
  venueDataImports,
  autoOpenImportId,
}: VenueDataImportsListProps) => {
  const [stagingResultsImport, setStagingResultsImport] =
    useState<VenueDataImport | null>(null);

  useEffect(() => {
    if (!autoOpenImportId || venueDataImports.length === 0) return;
    const match = venueDataImports.find((i) => i.id === autoOpenImportId && i.stagingResults);
    if (match) setStagingResultsImport(match);
  }, [autoOpenImportId, venueDataImports]);

  const [rollbackVenueDataImportMutation] = useMutation(
    rollbackVenueDataImport
  );

  const startRollback = (id: string) => {
    rollbackVenueDataImportMutation({
      variables: { input: { id } },
      onCompleted: () => window.location.reload(),
    });
  };

  const sorted = [...venueDataImports].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  return (
    <>
      <TableContainer
        sx={{
          border: '1px solid rgba(0, 0, 0, 0.1)',
          boxSizing: 'border-box',
        }}
      >
        <Table size="small">
          <TableHead
            sx={{
              color: 'rgba(0, 0, 0, 0.54)',
              backgroundColor: 'rgba(34, 34, 34, 0.02)',
            }}
          >
            <TableRow>
              <TableHeaderCell>Created At</TableHeaderCell>
              <TableHeaderCell>Updated At</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Error Messages</TableHeaderCell>
              <TableHeaderCell>Source</TableHeaderCell>
              <TableHeaderCell></TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((venueDataImport) => (
              <TableBodyRow key={venueDataImport.id}>
                <TableCell>{formatDate(venueDataImport.createdAt)}</TableCell>
                <TableCell>{formatDate(venueDataImport.updatedAt)}</TableCell>
                <TableCell>
                  <Chip
                    label={formatStatus(venueDataImport.status)}
                    color={statusColor(venueDataImport.status)}
                    variant="outlined"
                    size="small"
                  />
                </TableCell>
                <TableCell>{venueDataImport.errorMessages}</TableCell>
                <TableCell>
                  {venueDataImport.storageUrl && (
                    <a
                      href={venueDataImport.storageUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </a>
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    {venueDataImport.stagingResults &&
                      venueDataImport.status === 'completed' ? (
                        <Button
                          color="secondary"
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            setStagingResultsImport(venueDataImport)
                          }
                        >
                          View Import Results
                        </Button>
                      ) : venueDataImport.stagingResults ? (
                        <Button
                          color="secondary"
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            setStagingResultsImport(venueDataImport)
                          }
                        >
                          View Staging Results
                        </Button>
                      ) : null}
                    {(venueDataImport.status === 'completed' ||
                      venueDataImport.status === 'failed') && (
                      <Button
                        color="secondary"
                        variant="outlined"
                        size="small"
                        onClick={() => startRollback(venueDataImport.id)}
                      >
                        Rollback
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableBodyRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <StagingResultsDialog
        open={!!stagingResultsImport}
        onClose={() => setStagingResultsImport(null)}
        importId={stagingResultsImport?.id}
        status={stagingResultsImport?.status}
        stagingResults={stagingResultsImport?.stagingResults}
        storageUrl={stagingResultsImport?.storageUrl}
        createdAt={stagingResultsImport?.createdAt}
        onRollback={
          stagingResultsImport
            ? () => startRollback(stagingResultsImport.id)
            : undefined
        }
      />
    </>
  );
};

const TableHeaderCell = ({ children }: any) => {
  return (
    <TableCell>
      <Typography variant="overline">{children}</Typography>
    </TableCell>
  );
};

const TableBodyRow = ({ children }: any) => {
  return (
    <TableRow
      sx={{
        outline: '1px solid rgba(0, 0, 0, 0.01)',
        boxShadow: '0px 1px 0px 0px rgba(0, 0, 0, 0.1) inset',
      }}
    >
      {children}
    </TableRow>
  );
};
